// ─── Projects API (ADR 0039, GHE #1200) ───

import type {
  DataverseProjectLinkResult,
  DataverseProjectLinkState,
  DataverseProjectRowsPage,
} from '@a8-planner/shared';
import type {
  ApiListResponse,
  ApiResponse,
  CreatableClient,
  CreateProjectInput,
  CreateProjectMilestoneInput,
  CreateProjectPhaseInput,
  ApplyScheduleIntentInput,
  CreateExitCriterionInput,
  ApplyTemplateResult,
  ApplyTemplateTasksInput,
  ApplyTemplateTasksResult,
  ApproveLogEntryInput,
  CreateProjectBaselineInput,
  CreateProjectLogEntryInput,
  PhaseTemplateView,
  ProjectLogEntryView,
  SavePhaseTemplateInput,
  UpdateProjectLogEntryInput,
  ProjectBaselineVariance,
  ProjectBaselineView,
  GateDecisionInput,
  PhaseGateView,
  UpdateExitCriterionInput,
  HealthSuggestion,
  ProjectScheduleView,
  ScheduleIntent,
  ScheduleWriteResult,
  LinkTasksResult,
  ProjectStatusUpdateView,
  PublishProjectStatusInput,
  PhaseReservationView,
  SetPhaseReservationsInput,
  PhaseTaskItem,
  ProjectPhaseOption,
  Task,
  ProjectMilestoneView,
  SetProjectAccessInput,
  UpdateProjectInput,
  UpdateProjectMilestoneInput,
  ProjectDetail,
  ProjectPhaseView,
  ProjectResolved,
  ProjectSummary,
  UpdateProjectPhaseInput,
} from '@a8-planner/shared';
import { apiClient } from '../api-client';

export interface ProjectPortfolioParams {
  companyId?: string;
  clientId?: string;
  /** Only projects the caller can edit. */
  mine?: boolean;
  includeClosed?: boolean;
  /** D7 «Con alerta»: open projects ÁMBAR, ROJO or «Sin estado». */
  alert?: boolean;
  cursor?: string;
  limit?: number;
}

export const projectsApi = {
  /** The portfolio: only projects the caller may see. The server applies the rule; this narrows. */
  list(params: ProjectPortfolioParams = {}): Promise<ApiListResponse<ProjectSummary>> {
    const search = new URLSearchParams();
    if (params.companyId) search.set('companyId', params.companyId);
    if (params.clientId) search.set('clientId', params.clientId);
    if (params.mine) search.set('mine', 'true');
    if (params.includeClosed) search.set('includeClosed', 'true');
    if (params.alert) search.set('alert', 'true');
    if (params.cursor) search.set('cursor', params.cursor);
    if (params.limit != null) search.set('limit', String(params.limit));
    const qs = search.toString();
    return apiClient.get<ApiListResponse<ProjectSummary>>(`/projects${qs ? `?${qs}` : ''}`);
  },

  /** The clients the caller may create projects for — the create dialog offers only these. */
  creatableClients(companyId?: string): Promise<ApiResponse<CreatableClient[]>> {
    const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    return apiClient.get<ApiResponse<CreatableClient[]>>(`/projects/creatable-clients${qs}`);
  },

  create(input: CreateProjectInput): Promise<ApiResponse<ProjectDetail>> {
    return apiClient.post<ApiResponse<ProjectDetail>>('/projects', input);
  },

  /**
   * A `/app/p/{code}-{short}` ref → project id. Runs inside a route loader, so a 401 must not
   * trigger the client's global hard reload (same reason as `resolveApi`).
   */
  resolve(short: string): Promise<ApiResponse<ProjectResolved>> {
    return apiClient.get<ApiResponse<ProjectResolved>>(
      `/projects/resolve?short=${encodeURIComponent(short)}`,
      { suppressAuthRedirect: true },
    );
  },

  /** El vínculo Dataverse del proyecto: lo guardado y lo que la fila dice hoy (ADR 0039 D5). */
  getDataverseLink(projectId: string): Promise<ApiResponse<DataverseProjectLinkState>> {
    return apiClient.get<ApiResponse<DataverseProjectLinkState>>(
      `/dataverse/links/project/${projectId}`,
    );
  },

  /**
   * Las filas de Dataverse que se le pueden enlazar a este proyecto: sólo las de su cliente
   * (GHE #1443). Quien edita el proyecto, no sólo el superadmin.
   */
  dataverseRows(
    projectId: string,
    search: string,
  ): Promise<{ data: DataverseProjectRowsPage; meta: { nextCursor: string | null } }> {
    const q = new URLSearchParams({ limit: '25' });
    if (search) q.set('search', search);
    return apiClient.get(`/dataverse/links/project/${projectId}/rows?${q.toString()}`);
  },

  /** Enlazar, re-enlazar (otro `rowId`) o desenlazar (`null`). No escribe nada en Dataverse. */
  setDataverseLink(
    projectId: string,
    rowId: string | null,
  ): Promise<ApiResponse<DataverseProjectLinkResult>> {
    return apiClient.put<ApiResponse<DataverseProjectLinkResult>>(
      `/dataverse/links/project/${projectId}`,
      { rowId },
    );
  },

  get(id: string): Promise<ApiResponse<ProjectDetail>> {
    return apiClient.get<ApiResponse<ProjectDetail>>(`/projects/${id}`);
  },

  listPhases(projectId: string): Promise<ApiResponse<ProjectPhaseView[]>> {
    return apiClient.get<ApiResponse<ProjectPhaseView[]>>(`/projects/${projectId}/phases`);
  },

  createPhase(
    projectId: string,
    input: CreateProjectPhaseInput,
  ): Promise<ApiResponse<ProjectPhaseView>> {
    return apiClient.post<ApiResponse<ProjectPhaseView>>(`/projects/${projectId}/phases`, input);
  },

  updatePhase(
    phaseId: string,
    input: UpdateProjectPhaseInput,
  ): Promise<ApiResponse<ProjectPhaseView>> {
    return apiClient.patch<ApiResponse<ProjectPhaseView>>(`/project-phases/${phaseId}`, input);
  },

  reorderPhases(projectId: string, phaseIds: string[]): Promise<ApiResponse<ProjectPhaseView[]>> {
    return apiClient.put<ApiResponse<ProjectPhaseView[]>>(`/projects/${projectId}/phases/order`, {
      phaseIds,
    });
  },

  deletePhase(phaseId: string): Promise<void> {
    return apiClient.delete<void>(`/project-phases/${phaseId}`);
  },

  update(id: string, input: UpdateProjectInput): Promise<ApiResponse<ProjectDetail>> {
    return apiClient.patch<ApiResponse<ProjectDetail>>(`/projects/${id}`, input);
  },

  /** `data` is null when the saved configuration leaves the caller without access. */
  setAccess(
    id: string,
    input: SetProjectAccessInput,
  ): Promise<{ data: ProjectDetail | null; meta: { callerLostAccess: boolean } }> {
    return apiClient.put<{ data: ProjectDetail | null; meta: { callerLostAccess: boolean } }>(
      `/projects/${id}/access`,
      input,
    );
  },

  archive(id: string): Promise<void> {
    return apiClient.delete<void>(`/projects/${id}`);
  },

  listMilestones(projectId: string): Promise<ApiResponse<ProjectMilestoneView[]>> {
    return apiClient.get<ApiResponse<ProjectMilestoneView[]>>(`/projects/${projectId}/milestones`);
  },

  createMilestone(
    projectId: string,
    input: CreateProjectMilestoneInput,
  ): Promise<ApiResponse<ProjectMilestoneView>> {
    return apiClient.post<ApiResponse<ProjectMilestoneView>>(
      `/projects/${projectId}/milestones`,
      input,
    );
  },

  updateMilestone(
    milestoneId: string,
    input: UpdateProjectMilestoneInput,
  ): Promise<ApiResponse<ProjectMilestoneView>> {
    return apiClient.patch<ApiResponse<ProjectMilestoneView>>(
      `/project-milestones/${milestoneId}`,
      input,
    );
  },

  deleteMilestone(milestoneId: string): Promise<void> {
    return apiClient.delete<void>(`/project-milestones/${milestoneId}`);
  },

  // ─── Linking tasks to phases (rebanada 7) ───

  /** The phases a task of `clientId` can be linked to: visible, open projects of that client. */
  phaseOptions(clientId: string): Promise<ApiResponse<ProjectPhaseOption[]>> {
    return apiClient.get<ApiResponse<ProjectPhaseOption[]>>(
      `/projects/phase-options?clientId=${encodeURIComponent(clientId)}`,
    );
  },

  /** Link one task to a phase, or unlink it with `null`. Does not bump the task version. */
  setTaskPhase(taskId: string, phaseId: string | null): Promise<ApiResponse<Task>> {
    return apiClient.put<ApiResponse<Task>>(`/tasks/${taskId}/project-phase`, { phaseId });
  },

  linkTasks(phaseId: string, taskIds: string[]): Promise<ApiResponse<LinkTasksResult>> {
    return apiClient.post<ApiResponse<LinkTasksResult>>(`/project-phases/${phaseId}/tasks`, {
      taskIds,
    });
  },

  // ─── Phase templates (rebanada 13, D4) ───

  listPhaseTemplates(companyId: string): Promise<ApiResponse<PhaseTemplateView[]>> {
    return apiClient.get<ApiResponse<PhaseTemplateView[]>>(
      `/companies/${companyId}/phase-templates`,
    );
  },

  createPhaseTemplate(
    companyId: string,
    input: SavePhaseTemplateInput,
  ): Promise<ApiResponse<PhaseTemplateView>> {
    return apiClient.post<ApiResponse<PhaseTemplateView>>(
      `/companies/${companyId}/phase-templates`,
      input,
    );
  },

  /** Saved WHOLE: the phases sent replace the stored ones, in order. */
  savePhaseTemplate(
    templateId: string,
    input: SavePhaseTemplateInput,
  ): Promise<ApiResponse<PhaseTemplateView>> {
    return apiClient.put<ApiResponse<PhaseTemplateView>>(`/phase-templates/${templateId}`, input);
  },

  deletePhaseTemplate(templateId: string): Promise<void> {
    return apiClient.delete<void>(`/phase-templates/${templateId}`);
  },

  applyTemplate(projectId: string, templateId: string): Promise<ApiResponse<ApplyTemplateResult>> {
    return apiClient.post<ApiResponse<ApplyTemplateResult>>(
      `/projects/${projectId}/apply-template`,
      {
        templateId,
      },
    );
  },

  applyTemplateTasks(
    phaseId: string,
    input: ApplyTemplateTasksInput,
  ): Promise<ApiResponse<ApplyTemplateTasksResult>> {
    return apiClient.post<ApiResponse<ApplyTemplateTasksResult>>(
      `/project-phases/${phaseId}/template-tasks`,
      input,
    );
  },

  // ─── Register (rebanada 12, D10) ───

  listLog(
    projectId: string,
    filters: { type?: string; open?: boolean } = {},
  ): Promise<ApiResponse<ProjectLogEntryView[]>> {
    const search = new URLSearchParams();
    if (filters.type) search.set('type', filters.type);
    if (filters.open) search.set('open', 'true');
    const qs = search.toString();
    return apiClient.get<ApiResponse<ProjectLogEntryView[]>>(
      `/projects/${projectId}/log${qs ? `?${qs}` : ''}`,
    );
  },

  createLogEntry(
    projectId: string,
    input: CreateProjectLogEntryInput,
  ): Promise<ApiResponse<ProjectLogEntryView>> {
    return apiClient.post<ApiResponse<ProjectLogEntryView>>(`/projects/${projectId}/log`, input);
  },

  updateLogEntry(
    entryId: string,
    input: UpdateProjectLogEntryInput,
  ): Promise<ApiResponse<ProjectLogEntryView>> {
    return apiClient.patch<ApiResponse<ProjectLogEntryView>>(`/project-log/${entryId}`, input);
  },

  deleteLogEntry(entryId: string): Promise<void> {
    return apiClient.delete<void>(`/project-log/${entryId}`);
  },

  approveLogEntry(
    entryId: string,
    input: ApproveLogEntryInput,
  ): Promise<ApiResponse<ProjectLogEntryView>> {
    return apiClient.post<ApiResponse<ProjectLogEntryView>>(
      `/project-log/${entryId}/approval`,
      input,
    );
  },

  /** Ticked from the phase gate (D6). */
  reviewLogEntry(entryId: string, reviewed: boolean): Promise<ApiResponse<ProjectLogEntryView>> {
    return apiClient.post<ApiResponse<ProjectLogEntryView>>(`/project-log/${entryId}/review`, {
      reviewed,
    });
  },

  /**
   * Tasks of a client, by free text — the picker for a register entry's mitigation tasks. Scoped to
   * the project's client so the picker cannot offer work that could not be linked anyway.
   */
  searchClientTasks(clientId: string, q: string): Promise<ApiListResponse<Task>> {
    const search = new URLSearchParams({ clientIds: clientId, q, limit: '10' });
    return apiClient.get<ApiListResponse<Task>>(`/tasks?${search.toString()}`);
  },

  // ─── Baselines (rebanada 11) ───

  listBaselines(projectId: string): Promise<ApiResponse<ProjectBaselineView[]>> {
    return apiClient.get<ApiResponse<ProjectBaselineView[]>>(`/projects/${projectId}/baselines`);
  },

  createBaseline(
    projectId: string,
    input: CreateProjectBaselineInput,
  ): Promise<ApiResponse<ProjectBaselineView>> {
    return apiClient.post<ApiResponse<ProjectBaselineView>>(
      `/projects/${projectId}/baselines`,
      input,
    );
  },

  /** `null` stops comparing. A project compares against at most one baseline. */
  setComparedBaseline(
    projectId: string,
    baselineId: string | null,
  ): Promise<ApiResponse<ProjectBaselineView[]>> {
    return apiClient.put<ApiResponse<ProjectBaselineView[]>>(
      `/projects/${projectId}/compared-baseline`,
      { baselineId },
    );
  },

  baselineVariance(baselineId: string): Promise<ApiResponse<ProjectBaselineVariance>> {
    return apiClient.get<ApiResponse<ProjectBaselineVariance>>(
      `/project-baselines/${baselineId}/variance`,
    );
  },

  deleteBaseline(baselineId: string): Promise<void> {
    return apiClient.delete<void>(`/project-baselines/${baselineId}`);
  },

  // ─── Gates (rebanada 10, D6) ───

  getGate(phaseId: string): Promise<ApiResponse<PhaseGateView>> {
    return apiClient.get<ApiResponse<PhaseGateView>>(`/project-phases/${phaseId}/gate`);
  },

  addCriterion(
    phaseId: string,
    input: CreateExitCriterionInput,
  ): Promise<ApiResponse<PhaseGateView>> {
    return apiClient.post<ApiResponse<PhaseGateView>>(`/project-phases/${phaseId}/criteria`, input);
  },

  updateCriterion(
    criterionId: string,
    input: UpdateExitCriterionInput,
  ): Promise<ApiResponse<PhaseGateView>> {
    return apiClient.patch<ApiResponse<PhaseGateView>>(`/phase-criteria/${criterionId}`, input);
  },

  deleteCriterion(criterionId: string): Promise<ApiResponse<PhaseGateView>> {
    return apiClient.delete<ApiResponse<PhaseGateView>>(`/phase-criteria/${criterionId}`);
  },

  startPhase(phaseId: string, version: number): Promise<ApiResponse<PhaseGateView>> {
    return apiClient.post<ApiResponse<PhaseGateView>>(`/project-phases/${phaseId}/start`, {
      version,
    });
  },

  decideGate(phaseId: string, input: GateDecisionInput): Promise<ApiResponse<PhaseGateView>> {
    return apiClient.post<ApiResponse<PhaseGateView>>(
      `/project-phases/${phaseId}/gate-decisions`,
      input,
    );
  },

  // ─── Phase scheduler (rebanada 9, D2) ───

  getSchedule(projectId: string): Promise<ApiResponse<ProjectScheduleView>> {
    return apiClient.get<ApiResponse<ProjectScheduleView>>(`/projects/${projectId}/schedule`);
  },

  /** An INTENT against the version the caller saw; 409 when it moved. Never computed dates. */
  applySchedule(
    projectId: string,
    input: ApplyScheduleIntentInput,
  ): Promise<ApiResponse<ScheduleWriteResult>> {
    return apiClient.post<ApiResponse<ScheduleWriteResult>>(
      `/projects/${projectId}/schedule`,
      input,
    );
  },

  previewSchedule(
    projectId: string,
    intent: ScheduleIntent,
  ): Promise<ApiResponse<ProjectScheduleView>> {
    return apiClient.post<ApiResponse<ProjectScheduleView>>(
      `/projects/${projectId}/schedule/preview`,
      { intent },
    );
  },

  undoSchedule(projectId: string, version: number): Promise<ApiResponse<ScheduleWriteResult>> {
    return apiClient.post<ApiResponse<ScheduleWriteResult>>(
      `/projects/${projectId}/schedule/undo`,
      { version },
    );
  },

  // ─── Weekly status (rebanada 8, D7) ───

  listStatusUpdates(projectId: string): Promise<ApiResponse<ProjectStatusUpdateView[]>> {
    return apiClient.get<ApiResponse<ProjectStatusUpdateView[]>>(
      `/projects/${projectId}/status-updates`,
    );
  },

  publishStatus(
    projectId: string,
    input: PublishProjectStatusInput,
  ): Promise<ApiResponse<ProjectStatusUpdateView>> {
    return apiClient.post<ApiResponse<ProjectStatusUpdateView>>(
      `/projects/${projectId}/status-updates`,
      input,
    );
  },

  healthSuggestion(projectId: string): Promise<ApiResponse<HealthSuggestion>> {
    return apiClient.get<ApiResponse<HealthSuggestion>>(`/projects/${projectId}/health-suggestion`);
  },

  alertCount(companyId?: string): Promise<ApiResponse<{ count: number }>> {
    const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    return apiClient.get<ApiResponse<{ count: number }>>(`/projects/alert-count${qs}`);
  },

  // ─── Capacity reservations (rebanada 7b, D8) ───

  listReservations(projectId: string): Promise<ApiResponse<PhaseReservationView[]>> {
    return apiClient.get<ApiResponse<PhaseReservationView[]>>(
      `/projects/${projectId}/reservations`,
    );
  },

  /** Replaces the phase's WHOLE set: a person left out is no longer reserved. */
  setReservations(
    phaseId: string,
    input: SetPhaseReservationsInput,
  ): Promise<ApiResponse<PhaseReservationView[]>> {
    return apiClient.put<ApiResponse<PhaseReservationView[]>>(
      `/project-phases/${phaseId}/reservations`,
      input,
    );
  },

  /** Linked tasks the caller can open (the phase figures count the rest as numbers only). */
  listPhaseTasks(phaseId: string): Promise<ApiResponse<PhaseTaskItem[]>> {
    return apiClient.get<ApiResponse<PhaseTaskItem[]>>(`/project-phases/${phaseId}/tasks`);
  },
};
